import { CommonModule, DOCUMENT } from '@angular/common';
import type { AfterViewInit, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Component, inject, Input, PLATFORM_ID, QueryList, ViewChildren } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { CouponAggregationService } from '@core-mkt/services/coupon-aggregation/coupon-aggregation.service';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { EnvService } from '@core-mkt/services/env/env.service';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WebinarScheduleService } from '@core-mkt/services/webinar-schedule/webinar-schedule.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { XGritApiService } from '@core-mkt/services/xgrit-api/xgrit-api.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMinus, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TierBadgeComponent } from '../../tier-badge/tier-badge.component';
import { VisionButtonComponent } from '../../vision-button/vision-button/vision-button.component';
import { VisionModalService } from '../../vision-modal/vision-modal.service';
import type { WebinarScheduleContent, WebinarScheduleResponse } from '../../webinar-schedule/webinar-schedule';
import type { BundleAccordionItem, BundleItem } from './bundle';
import { Bundle } from './bundle';
import { getBundleSchema } from './bundle.schema';
import { COUPON_SCROLL_TARGET } from '@core-mkt/services/coupon-scroll/coupon-scroll.service';

@Component({
  selector: 'bundle',
  templateUrl: './bundle.component.html',
  styleUrls: ['./bundle.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    VisionButtonComponent,
    LazyImgDirective,
    QueryParamDirective,
    TierBadgeComponent,
  ],
})
export class BundleComponent implements OnInit, AfterViewInit, OnDestroy {
  // FREC rule 61J2-17.013(1) prohibits exam-pass guarantees on FL real-estate products (EPS-1111);
  // guarantee text is hidden only when the brand AND the product state are both in these lists
  static readonly GUARANTEE_SUPPRESSED_BRANDS: readonly string[] = ['AA'];
  static readonly GUARANTEE_SUPPRESSED_STATES: readonly string[] = ['FL'];

  // BNPL (Buy Now Pay Later) calculation constants
  static readonly BNPL_THRESHOLD = 100; // Minimum price to show BNPL messaging
  private static readonly AFFIRM_INTEREST_RATE = 1.085; // 8.5% annual interest rate
  private static readonly AFFIRM_PAYMENT_MONTHS = 12; // 12-month payment plan
  private static readonly KLARNA_PAYMENT_INSTALLMENTS = 4; // 4 equal installments

  @Input() data: Bundle;
  @ViewChildren('bundlePrice') bundlePrice: QueryList<ElementRef>;
  theme: ThemeInterface;
  faPlus = faPlus;
  faMinus = faMinus;
  faTimes = faTimes;
  showModal = false;
  couponApplied = false;
  activeCoupon = '';
  additionalCoupon: string; // Used to store additional coupon code from the cookie
  closeIcon: SafeHtml | null;
  openIcon: SafeHtml | null;
  topContent: SafeHtml | null;
  urlCouponPresent: boolean[] = [];
  private destroy$ = new Subject<void>();
  guaranteeTextOptions!: {
    moneyBack: {
      text: string;
      modalTitle: string;
      modalBody: string;
    };
    satisfactionGuarantee: {
      text: string;
      modalTitle: string;
      modalBody: string;
    };
  };

  globalCDIQueryParam: string;
  globalSalesQueryParam: string;
  public hasProducts = false;
  public hasBnplEligibleProducts = false;

  private readonly themeParser = inject(ThemeParserService);
  private readonly redactor = inject(WysiwygRedactorService);
  private readonly ps = inject(ProductService);
  private readonly pricingService = inject(PricingService);
  private readonly IconService = inject(IconService);
  private readonly env = inject(EnvService);
  private readonly modalService = inject(VisionModalService);
  private readonly webinarScheduleService = inject(WebinarScheduleService);
  private readonly xas = inject(XGritApiService);
  private readonly checkoutUrlBuilderService = inject(CheckoutUrlBuilderService);
  private readonly couponQueryResolverService = inject(CouponQueryResolverService);
  private readonly couponAggregationService = inject(CouponAggregationService);
  private readonly schemaService = inject(SchemaService);
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject<Document>(DOCUMENT);

  constructor() {
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);
  }

  get bnplThreshold(): number {
    return BundleComponent.BNPL_THRESHOLD;
  }

  get bundles(): BundleItem[] {
    return this.data.bundles;
  }

  /** Marks this component as an email-capture scroll-back target. */
  readonly couponScrollTarget = COUPON_SCROLL_TARGET.bundle;

  /** A price has rendered, so this is a real email-capture scroll-back target (EPS-4801). */
  get hasPricedBundle(): boolean {
    return this.bundles?.some((b) => !!b.originalPrice) ?? false;
  }

  get accordions(): BundleAccordionItem[][] {
    return [this.data.bundleAccordionItems1, this.data.bundleAccordionItems2, this.data.bundleAccordionItems3];
  }

  get guaranteeText(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].text;
  }

  get guaranteeModalTitle(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].modalTitle;
  }

  get guaranteeModalBody(): string {
    return this.guaranteeTextOptions[this.data.guaranteeText as keyof typeof this.guaranteeTextOptions].modalBody;
  }

  get showFee(): boolean {
    return this.env.get.brandConfig.showFee;
  }

  get feeLabel(): string {
    return this.env.get.brandConfig.feeLabel;
  }

  get vertical(): string {
    return this.env.get.brandConfig?.vertical?.replace('-', ' ');
  }

  get isAceDesign(): boolean {
    return (this.env.get.brandConfig.designMode ?? 'ace') === 'ace';
  }

  suppressGuarantee(product?: CraftXGritProductEntry): boolean {
    return (
      BundleComponent.GUARANTEE_SUPPRESSED_BRANDS.includes(this.env.get.brandConfig.id ?? '') &&
      BundleComponent.GUARANTEE_SUPPRESSED_STATES.includes(product?.productState ?? '')
    );
  }

  public CustomDecimal(value: number): string {
    if (value % 1 === 0) {
      return value.toFixed(0);
    } else {
      return value.toFixed(2);
    }
  }

  ngOnInit(): void {
    // Initialize guarantee text options
    this.guaranteeTextOptions = {
      moneyBack: {
        text: 'Pass the test or your money back!',
        modalTitle: `Ace or Don't Pay Guarantee`,
        modalBody: `Our online ${this.vertical} license course is so effective that we guarantee if you don't pass the licensing exam on your first try, we'll refund your money. Additional terms and conditions apply. State laws and regulations may prohibit some students from qualifying. Review our refund policy to learn more.`,
      },
      satisfactionGuarantee: {
        text: '30-Day Satisfaction Guarantee',
        modalTitle: `30-Day Satisfaction Guarantee!`,
        modalBody: `Try ${this.env.get.brandConfig.name} for 30 days. If you're not satisfied and haven't completed your course, we'll refund your money. Review our refund policy to learn more.`,
      },
    };

    this.data.guaranteeText = this.data.guaranteeText || 'moneyBack';
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: '',
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);

    this.schemaService.register(getBundleSchema(this.data.bundles, this.document.URL));

    this.topContent = this.redactor.bypassSanitizer(this.data.topContent);
    this.openIcon = this.redactor.bypassSanitizer(this.IconService.getBrandIcon('accordion-open')?.icon);
    this.closeIcon = this.redactor.bypassSanitizer(this.IconService.getBrandIcon('accordion-close')?.icon);

    this.data.bundles.forEach((b) => {
      b.principalContent = this.redactor.bypassSanitizer(b.principalContent);
      b.bottomContent = this.redactor.bypassSanitizer(b.bottomContent);
    });

    this.processAccordionItems(this.data.bundleAccordionItems1);
    this.processAccordionItems(this.data.bundleAccordionItems2);
    this.processAccordionItems(this.data.bundleAccordionItems3);

    // Check if a capture coupon already exists (user returning to page after email/phone capture)
    const couponCollection = this.couponAggregationService.collectAllCoupons();
    const existingCaptureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

    if (existingCaptureCoupon?.trim()) {
      // Capture coupon exists from previous session - show "Coupon Applied!" badge
      this.couponApplied = true;
      this.additionalCoupon = existingCaptureCoupon;
    }

    // Keep coupon event subscription for email/phone capture animated pricing
    this.pricingService.couponCodeEvent.pipe(takeUntil(this.destroy$)).subscribe((coupon: string | null) => {
      if (coupon && coupon.trim() !== '') {
        // Check if an email or phone capture coupon was just applied
        const couponCollection = this.couponAggregationService.collectAllCoupons();
        const captureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

        if (captureCoupon?.trim()) {
          // Capture coupon found - use animated pricing for better UX
          this.additionalCoupon = captureCoupon;
          this.animateEmailCapturePrice();
        } else {
          // Standard coupon event handling
          this.activeCoupon = `&${this.globalCDIQueryParam}=` + coupon;
          this.additionalCoupon = coupon;
          this.adjustPricing(coupon);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Make single API call - CouponAggregationService automatically collects banner coupon
    // via getCurrentOverrideCoupon() or route data fallback
    this.getProducts(this.data.bundles);

    /**
     * Detect when ViewChildren bundlePrice changes and
     * adjust pricing if coupon is already applied
     */
    this.bundlePrice.changes.subscribe(() => {
      // 2nd condition prevent emailCapture coupon to override ongoing sale
      if (this.activeCoupon && this.urlCouponPresent.every((isPresent) => !isPresent)) {
        // If activeCoupon is already set, adjust pricing
        this.adjustPricing(this.additionalCoupon);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processAccordionItems(items: BundleAccordionItem[]): void {
    items.forEach((i: BundleAccordionItem) => {
      i.itemDescription = this.redactor.bypassSanitizer(i.itemDescription);
    });
  }

  /**
   * Animates pricing specifically for email capture coupons using CouponAggregationService
   */
  private animateEmailCapturePrice(): void {
    this.bundlePrice.forEach((el: ElementRef<HTMLDivElement>, index) => {
      const bundle = this.data.bundles[index];
      const product = this.ps.extractProduct(bundle.product);
      // ProductService uses CouponAggregationService to handle all coupons
      this.ps
        .getProductData(product)
        .then((product: XGritCompleteProduct) => {
          const start = product.apiData.originalPrice;
          const end = product.apiData.discountPrice;
          const maxPrice = product.apiData.maxPrice;
          const discountPrice = product.apiData.discountPrice;
          bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
          bundle.maxPrice = maxPrice;
          bundle.originalPrice = discountPrice;
          bundle.discountType = product.apiData.discountType;

          this.couponApplied = true;
          if (start === end) return;
          let current = start;
          const stepTime = 14;
          const timer = setInterval(() => {
            current -= 1;
            el.nativeElement.innerHTML = '$' + current.toString();
            if (current <= end) {
              clearInterval(timer);
              return;
            }
          }, stepTime);
        })
        .catch(() => {
          // Email capture pricing animation failed - fallback to instant update
          this.getProducts(this.data.bundles);
        });
    });
  }

  public adjustPricing(coupon: string): void {
    const promises: Promise<void>[] = [];
    this.bundlePrice.forEach((el: ElementRef<HTMLDivElement>, index) => {
      const bundle = this.data.bundles[index];
      const product = this.ps.extractProduct(bundle.product);
      product.xgritCoupon = coupon;
      const promise = this.ps
        .getProductData(product)
        .then((product: XGritCompleteProduct) => {
          const start = product.apiData.originalPrice;
          const end = product.apiData.discountPrice;
          const maxPrice = product.apiData.maxPrice;
          const discountPrice = product.apiData.discountPrice;
          bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
          bundle.maxPrice = maxPrice;
          bundle.originalPrice = discountPrice;
          bundle.discountType = product.apiData.discountType;

          // Calculate payment options based on final discounted price
          bundle.affirmPrice = Math.ceil(
            discountPrice * (BundleComponent.AFFIRM_INTEREST_RATE / BundleComponent.AFFIRM_PAYMENT_MONTHS),
          );
          bundle.klarnaPrice = +(discountPrice / BundleComponent.KLARNA_PAYMENT_INSTALLMENTS).toFixed(2);

          this.couponApplied = true;
          if (start === end) return;
          let current = start;
          const stepTime = 14;
          const timer = setInterval(() => {
            current -= 1;
            el.nativeElement.innerHTML = '$' + current.toString();
            if (current <= end) {
              clearInterval(timer);
              return;
            }
          }, stepTime);
        })
        .catch(() => {
          // Pricing adjustment failed - component continues to function
        });
      promises.push(promise);
    });
    void Promise.allSettled(promises).then(() => {
      this.hasBnplEligibleProducts = this.data.bundles.some((b) => b.originalPrice >= BundleComponent.BNPL_THRESHOLD);
    });
  }

  activateModal(modalContent: string): void {
    this.modalService.openModal({ modalContent: modalContent, showModal: true });
  }

  /**
   * Displays the webinar schedule by triggering the webinar schedule service.
   *
   * @param event - The event object associated with the user interaction.
   * @param content - The content to be passed to the webinar schedule service.
   *
   * @remarks
   * This method prevents the default behavior of the event before invoking the service.
   */
  showWebinarSchedule(event: Event, content: WebinarScheduleResponse[]): void {
    event.preventDefault();
    this.webinarScheduleService.setShowWebinarSchedule(true, content);
  }

  toggle(accordionItem: BundleAccordionItem): void {
    accordionItem.showDescription = !accordionItem.showDescription;
  }

  toggleModal(): void {
    this.showModal = !this.showModal;
  }

  showAccordion(index: number): boolean {
    return !!this.accordions[index]?.[0]?.itemTitle;
  }

  getButtonData(bundleItem: BundleItem): VisionButton {
    // Normalize the host first: a non-checkout handoff (AARP's course-selection) skips the builder below.
    const buttonLink = this.checkoutUrlBuilderService.toEnvCheckoutHost(
      bundleItem.ctaLinkOverride || bundleItem.ctaLink || bundleItem.productCheckoutUrl || '',
    );

    let finalButtonLink: string;

    // Use enhanced coupon aggregation for checkout URLs
    if (buttonLink?.toLowerCase().includes('checkout')) {
      finalButtonLink = this.checkoutUrlBuilderService.buildCheckoutUrlWithAllCoupons(buttonLink);
    } else {
      // For non-checkout URLs, apply legacy coupon logic if needed
      const urlCouponPresent = buttonLink ? buttonLink.includes(this.globalCDIQueryParam) : false;
      finalButtonLink = `${buttonLink}${urlCouponPresent ? '' : this.activeCoupon}`;
    }

    return {
      bgTheme: this.theme.root,
      buttonLink: finalButtonLink,
      buttonText: bundleItem.ctaLabel || this.env.get.brandConfig.ctaText,
      buttonAriaLabel: bundleItem.ctaLinkAriaLabel ? bundleItem.ctaLinkAriaLabel : undefined,
      buttonStyle: bundleItem.ctaStyle,
      newTab: bundleItem.ctaLinkNewTab,
      nofollow: bundleItem.ctaLinkNofollow,
      noopener: bundleItem.ctaLinkNewTab,
      noreferrer: bundleItem.ctaLinkNewTab,
      fullWidth: true,
    };
  }

  get viewScheduleLabel(): string {
    return 'View Schedule';
  }

  /**
   * Sets the flag indicating whether products are available.
   * @param hasProducts - Boolean flag indicating if products exist
   */
  setHasProducts(hasProducts: boolean): void {
    this.hasProducts = hasProducts;
  }

  getProducts(bundles: BundleItem[]): void {
    const craftProducts: CraftXGritProductEntry[] = [];
    bundles.forEach((bundle) => {
      if (this.ps.isProductValid(bundle.product)) {
        craftProducts.push(this.ps.extractProduct(bundle.product));
      }
    });
    // If no valid products are found, exit early and set the `hasProducts` status to false
    if (craftProducts.length === 0) {
      this.setHasProducts(false);
      this.hasBnplEligibleProducts = false;
      return;
    } else {
      this.setHasProducts(true);
    }
    this.ps
      .getMultiProductData(craftProducts)
      .then((xgritProducts) => {
        if (xgritProducts.length !== 0) {
          bundles.forEach((bundle: BundleItem, index) => {
            const webinarTitle = bundle.product[0]?.webinarTitle as string;
            const webinarContent = bundle.product[0]?.webinarContent as string;
            // If webinarSchedule is present on the product entry, we need to get the webinar data
            const hasWebinarCourseIds =
              bundle.product[0]?.webinarCourseIds &&
              bundle.product[0]?.webinarCourseIds.length > 0 &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              bundle.product[0]?.webinarCourseIds.filter(
                (courseId) =>
                  courseId !== null && courseId !== undefined && courseId.courseid && courseId.courseid.length > 0,
              ).length > 0;
            if (hasWebinarCourseIds) {
              // If the product entry has valid webinarCourseIds, get the webinar data
              // Pass the entire product entry to let the service determine the appropriate category
              this.xas
                .getWebinarSchedule(
                  bundle.product[0]?.webinarCourseIds as WebinarScheduleContent[],
                  bundle.product[0] as CraftXGritProductEntry,
                  webinarTitle,
                  webinarContent,
                )
                .subscribe({
                  next: (res: WebinarScheduleResponse[]) => {
                    bundle.webinarData = res;
                  },
                  error: () => {
                    // Webinar schedule API failed - component continues without webinar data
                    bundle.webinarData = [];
                  },
                });
            }

            const maxPrice = xgritProducts[index]?.apiData.maxPrice;
            const originalPrice = xgritProducts[index]?.apiData.originalPrice;
            const discountPrice = xgritProducts[index]?.apiData.discountPrice;
            bundle.productCheckoutUrl = xgritProducts[index]?.apiData.checkoutLink;
            bundle.discountType = xgritProducts[index]?.apiData.discountType;
            bundle.ctaLinkAriaLabel = xgritProducts[index]?.apiData.title;
            bundle.feeModalContent = xgritProducts[index]?.apiData.feeDescription;
            bundle.productFees = xgritProducts[index]?.apiData.productFees;

            // Recording every time a product has a couponCode implying there's an ongoing sale
            this.urlCouponPresent.push(
              xgritProducts[index]?.apiData.checkoutLink && xgritProducts[index]?.craftData.xgritCoupon
                ? xgritProducts[index]?.apiData.checkoutLink.includes(this.globalCDIQueryParam) &&
                    xgritProducts[index]?.apiData.checkoutLink.includes(xgritProducts[index]?.craftData.xgritCoupon)
                : false,
            );
            if (
              xgritProducts[index]?.apiData.maxPrice &&
              xgritProducts[index]?.apiData.maxPrice !== xgritProducts[index]?.apiData.originalPrice
            ) {
              // Show Save % and both prices
              bundle.savePercentage = Math.round(((maxPrice - originalPrice) / maxPrice) * 100);
              bundle.maxPrice = maxPrice;
              bundle.originalPrice = originalPrice;
            } else if (
              xgritProducts[index]?.apiData.maxPrice &&
              xgritProducts[index]?.apiData.discountPrice &&
              xgritProducts[index]?.apiData.maxPrice === xgritProducts[index]?.apiData.originalPrice
            ) {
              // Show Save % and both prices
              bundle.savePercentage = Math.round(((maxPrice - discountPrice) / maxPrice) * 100);
              bundle.maxPrice = maxPrice;
              bundle.originalPrice = discountPrice;
            } else {
              // Only final price
              bundle.originalPrice = originalPrice;
            }

            // Use discountPrice to calculate monthly payments, as it is the final price after all discounts
            bundle.affirmPrice = Math.ceil(
              discountPrice * (BundleComponent.AFFIRM_INTEREST_RATE / BundleComponent.AFFIRM_PAYMENT_MONTHS),
            );
            bundle.klarnaPrice = +(discountPrice / BundleComponent.KLARNA_PAYMENT_INSTALLMENTS).toFixed(2);
          });
          this.hasBnplEligibleProducts = bundles.some((b) => b.originalPrice >= BundleComponent.BNPL_THRESHOLD);
        }
      })
      .catch(() => {
        // Error fetching multi-product data - component continues to function
        this.hasBnplEligibleProducts = false;
      });
  }
}
